import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomChannelList from "../SendbirdCustomComponents/CustomChannelList";
import CustomChannelListHeader from "../SendbirdCustomComponents/CustomChannelListHeader";
import CustomChannelPreview from "../SendbirdCustomComponents/CustomChannelPreview";
import CustomConversationHeader from "../SendbirdCustomComponents/CustomConversationHeader";
import CustomMessageInput from "../SendbirdCustomComponents/CustomMessageInput";
import CustomMessageItem from "../SendbirdCustomComponents/CustomMessageItem";
import "./messenger.scss";
import { withTranslation } from "react-i18next";
import ContactInfo from "../SendbirdCustomComponents/ContactInfo";
import Loader from "components/Loader";
import ImageViewer from "../Modals/ImageViewer";
import useConversation from "Messenger/hooks/conversation/useConversation";
import constants from "../../../../../constants";
import ChatClosed from "Messenger/pages/PatientConversation/component/ChatClosed";
import ChatLoader from "Messenger/chatLoader";
import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import Text from "components/Text";
import { LoaderIcon } from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import { cacheSideBarActive, getStorage, sortAlphabetically } from "utils";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import {
  applySelectionChanges,
  buildCustomChannelListHeaderProps,
  buildCustomChannelListProps,
  buildCustomConversationHeaderProps,
  resolveNoChatDetail,
} from "Messenger/pages/TeamConversation/components/TabsContent/internalTabContent.helpers";

import { GroupChannelListProvider } from "@sendbird/uikit-react/GroupChannelList/context";
import { GroupChannelProvider } from "@sendbird/uikit-react/GroupChannel/context";
import { GroupChannelUI } from "@sendbird/uikit-react/GroupChannel/components/GroupChannelUI";
import { useOwnerSpecificStaffDesignation } from "repositories/scheduler-repository";
const InternalTabContent = ({
  t,
  currentUser,
  selectedOwner,
  currentChannel,
  setCurrentChannel,
  externalTabActive,
  internalTabActive,
  setViewAppointmentCred,
  doctorChatCheck,
  isFromPatientChat = false,
  /** When false, child list must not clear shared `currentChannel` (inactive tab still mounts under TabContent). */
  isTabActive = true,
  /** Channel URL opened from a web-push deep link; must not be cleared by list filters. */
  pinnedChannelUrl = "",
}) => {
  const [callNextList, setCallNextList] = useState(0);
  const [hasMoreOffices, setHasMoreOffices] = useState(false);
  const filteredOfficesLocal = useMemo(() => {
    const value = getStorage("isFromOwnerOfficeModule")?.officeId;
    if (value) {
      localStorage.setItem(
        constants.localStorageKeys.persistOfficeFilter,
        JSON.stringify([value]),
      );
      return [
        { event: { target: { checked: true } }, id: value, isChecked: true },
      ];
    }
    return [];
  }, []);
  const [selected, setSelected] = useState({
    selection: filteredOfficesLocal,
    applied: filteredOfficesLocal,
  });
  const [roleSelected, setRoleSelected] = useState({
    selection: [],
    applied: [],
  });

  const { data: ownerSpecificDesignationData } =
    useOwnerSpecificStaffDesignation(
      { selectedId: selectedOwner?.id },
      { enabled: !!selectedOwner?.id && internalTabActive === true },
    );

  const roleFilterOptions = useMemo(() => {
    const roles = ownerSpecificDesignationData?.designations_list || [];
    const opts = roles.map((role) => ({
      value: role.id,
      label: role.name || role.title || "",
      name: `role_${role.id}`,
    }));
    return [
      {
        value: 0,
        label: t("accountOwner.allRoles"),
        name: "role_all",
      },
      ...sortAlphabetically(opts, "label"),
    ];
  }, [ownerSpecificDesignationData, t]);

  const showRoleFilter =
    internalTabActive === true && roleFilterOptions.length > 1;

  const { state, otherData, updateMethods, otherMethods } = useConversation({
    currentUser,
    currentChannel,
    setCurrentChannel,
    filteredOfficesLocal,
    selectedOwner,
    ...(internalTabActive !== undefined && {
      isInternalChat: internalTabActive,
    }),
    ...(externalTabActive !== undefined && {
      isExternalChat: externalTabActive,
    }),
    t,
  });
  const {
    profileContactInfo,
    channelListQuery,
    fileMessage,
    fileViewer,
    filteredOffices,
    filteredOfficesForNew,
    searchText,
    selectedOption,
    filteredRoleIds,
    staffDesignationIdsByUserId,
    staffRoleLookupBusy,
  } = state;

  const { isSdkLoading, isUserLoading, sdk, isSdkError } = otherData;
  const {
    setChannelListQuery,
    setFileMessage,
    setFileViewer,
    setFilteredOffices,
    setFilteredRoleIds,
    updateChannelList,
    setSearchText,
    setFilteredOfficesForNew,
    setSelectedOption,
  } = updateMethods;
  const {
    handleProfileBtn,
    handleProfileBtnClose,
    handleChannelSelect,
    handleBackBtn,
    handleOnBeforeSendUserMessage,
    sendUserMessage,
    sendFileMessage,
    handleOnBeforeSendFileMessage,
    handleRequestStatusFilter,
  } = otherMethods;
  const doctorChatCheckExistInProps = doctorChatCheck !== undefined;
  const isChatClosed =
    doctorChatCheckExistInProps &&
    currentChannel?.customType === constants.chat.patient.close;
  const [allChannelsOfficesForDoctor, setAllChannelsOfficesForDoctor] =
    useState([]);
  const [officeListLoading, setOfficeListLoading] = useState(false);

  const customChannelListProps = useMemo(() => {
    return buildCustomChannelListProps({
      internalTabActive,
      externalTabActive,
      filteredOffices,
      filteredRoleIds,
      staffDesignationIdsByUserId,
      staffRoleLookupBusy,
      selectedOwner,
      doctorChatCheckExistInProps,
      doctorChatCheck,
      setAllChannelsOfficesForDoctor,
      searchText,
      setCurrentChannel,
      isFromPatientChat,
    });
  }, [
    filteredOffices,
    filteredRoleIds,
    staffDesignationIdsByUserId,
    staffRoleLookupBusy,
    selectedOwner,
    doctorChatCheckExistInProps,
    doctorChatCheck,
    setAllChannelsOfficesForDoctor,
    searchText,
    setCurrentChannel,
    externalTabActive,
    internalTabActive,
    isFromPatientChat,
  ]);

  const customChannelListHeaderProps = useMemo(() => {
    return buildCustomChannelListHeaderProps({
      internalTabActive,
      externalTabActive,
      doctorChatCheckExistInProps,
      doctorChatCheck,
      allChannelsOfficesForDoctor,
      filteredOffices,
      searchText,
      setSearchText,
      selectedOption,
      setSelectedOption,
      handleRequestStatusFilter,
      setFilteredOfficesForNew,
      filteredOfficesForNew,
    });
  }, [
    filteredOffices,
    externalTabActive,
    doctorChatCheck,
    doctorChatCheckExistInProps,
    allChannelsOfficesForDoctor,
    searchText,
    setSearchText,
    selectedOption,
    setSelectedOption,
    handleRequestStatusFilter,
    setFilteredOfficesForNew,
    filteredOfficesForNew,
    internalTabActive,
  ]);
  const customConversationHeaderProps = useMemo(() => {
    return buildCustomConversationHeaderProps({
      externalTabActive,
      doctorChatCheckExistInProps,
      doctorChatCheck,
      setViewAppointmentCred,
    });
  }, [
    externalTabActive,
    doctorChatCheckExistInProps,
    doctorChatCheck,
    setViewAppointmentCred,
  ]);
  const noChatDetail = useMemo(() => {
    return resolveNoChatDetail({
      t,
      internalTabActive,
      externalTabActive,
      doctorChatCheck,
    });
  }, [doctorChatCheck, internalTabActive, externalTabActive, t]);
  const chacheData = isFromPatientChat
    ? constants.sidebar.cache.patientChatIsSidebarActive
    : constants.sidebar.cache.teamChatIsSidebarActive;
  const [isSidebarActive, setSidebarActive] = useState(
    cacheSideBarActive(chacheData),
  );

  const handleSidebarToggle = useCallback(() => {
    setSidebarActive((prev) => {
      const nextValue = !prev;
      localStorage.setItem(chacheData, nextValue);
      return nextValue;
    });
  }, [chacheData]);

  const handleApply = useCallback(() => {
    setSelected((prev) => {
      return {
        ...prev,
        applied: [...prev.selection],
      };
    });
    setRoleSelected((prev) => {
      const applied = [...prev.selection];
      if (!applied.length) {
        setFilteredRoleIds([]);
        localStorage.setItem(
          constants.localStorageKeys.persistRoleFilter,
          JSON.stringify([]),
        );
      }
      return {
        ...prev,
        applied,
      };
    });
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  }, [handleSidebarToggle, setFilteredRoleIds]);

  const handleReset = useCallback(() => {
    setSelected((prev) => {
      return {
        ...prev,
        applied: prev.applied.map((res) => ({
          ...res,
          isChecked: false,
        })),
        selection: [],
      };
    });
    setRoleSelected((prev) => ({
      ...prev,
      applied: prev.applied.map((res) => ({
        ...res,
        isChecked: false,
      })),
      selection: [],
    }));
    setFilteredRoleIds([]);
    localStorage.setItem(
      constants.localStorageKeys.persistRoleFilter,
      JSON.stringify([]),
    );
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    if (mediaQuery.matches) handleSidebarToggle();
  }, [handleSidebarToggle, setFilteredRoleIds]);

  const preservedRef = useRef(null);
  useEffect(() => {
    if (preservedRef.current === null) {
      preservedRef.current = selectedOwner?.id;
    } else if (preservedRef.current !== selectedOwner?.id) {
      handleReset();
      preservedRef.current = selectedOwner?.id;
    }
  }, [handleReset, selectedOwner?.id]);
  const onChangeHandler = useCallback((of, e) => {
    setSelected((prev) => {
      return applySelectionChanges(prev, of, e);
    });
  }, []);

  const onRoleChangeHandler = useCallback((roleOption, e) => {
    setRoleSelected((prev) => {
      return applySelectionChanges(prev, roleOption, e);
    });
  }, []);

  const hasActiveTeamFilters = useMemo(() => {
    const officeActive =
      Array.isArray(filteredOffices) &&
      filteredOffices.length > 0 &&
      !filteredOffices.includes(constants.chat.ALL_OFFICES);
    const roleActive =
      internalTabActive === true &&
      Array.isArray(filteredRoleIds) &&
      filteredRoleIds.length > 0 &&
      !filteredRoleIds.includes(constants.chat.ALL_ROLES);
    return officeActive || roleActive;
  }, [filteredOffices, filteredRoleIds, internalTabActive]);

  return (
    <div
      className={` ${
        externalTabActive ? "miraxis-messenger-external" : null
      }  ${
        isChatClosed
          ? `miraxis_messenger_new miraxis-messenger `
          : `miraxis-messenger`
      }`}
    >
      {(isSdkLoading || isUserLoading) && <Loader />}
      {/* Chat list */}
      {!externalTabActive && (
        <StickySidebar
          isSidebarActive={isSidebarActive}
          handleSidebarToggle={handleSidebarToggle}
          resetFilter={handleReset}
        >
          <div className="sidebar-content">
            <div className="team-chat-filters-heading">
              <Text size="15px" weight="600" color="#102C42">
                {t("taskManagement.filters")}
              </Text>
              {hasActiveTeamFilters && (
                <span
                  className="team-chat-filters-heading__dot"
                  title={t("messenger.filtersActiveIndicator")}
                  aria-label={t("messenger.filtersActiveIndicator")}
                />
              )}
            </div>
            <Accordion
              preExpanded={showRoleFilter ? ["a", "b"] : ["a"]}
              className="filter-accordion"
              allowZeroExpanded
              allowMultipleExpanded
            >
              <AccordionItem uuid="a">
                <AccordionItemHeading>
                  <AccordionItemButton>
                    {t("taskManagement.offices")}
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div className="filter-list override-height">
                    <InfiniteScroll
                      dataLength={allChannelsOfficesForDoctor?.length}
                      hasMore={doctorChatCheck ? false : hasMoreOffices}
                      next={() => setCallNextList((prev) => prev + 1)}
                      scrollableTarget="offices-list"
                      loader={<LoaderIcon style={{ margin: "0 auto" }} />}
                      scrollThreshold={0.5}
                      className="team_chat_offices"
                    >
                      {allChannelsOfficesForDoctor.map((of) => (
                        <div
                          className={`${["ch-checkbox"]} ${["team-chat-typo-box"]}`}
                          key={of.value}
                          title={of.label}
                        >
                          <label>
                            <input
                              value={of.value}
                              name={of.name}
                              checked={
                                !of.value
                                  ? selected.selection.every(
                                      (res) => !res.isChecked,
                                    )
                                  : selected.selection?.some(
                                      (res) =>
                                        res.id === of.value && res.isChecked,
                                    )
                              }
                              onChange={(event) => onChangeHandler(of, event)}
                              type="checkbox"
                            />
                            <span> {of.label} </span>
                          </label>
                        </div>
                      ))}
                      {officeListLoading && (
                        <LoaderIcon style={{ margin: "0 auto" }} />
                      )}
                    </InfiniteScroll>
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
              {showRoleFilter && (
                <AccordionItem uuid="b">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      {t("taskManagement.roles")}
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div
                      className="filter-list override-height"
                      id="team-chat-roles-scroll"
                    >
                      <InfiniteScroll
                        dataLength={roleFilterOptions.length}
                        hasMore={false}
                        next={() => {}}
                        scrollableTarget="team-chat-roles-scroll"
                        loader={null}
                        scrollThreshold={0.5}
                        className="team_chat_offices"
                      >
                        {roleFilterOptions.map((ro) => (
                          <div
                            className={`${["ch-checkbox"]} ${["team-chat-typo-box"]}`}
                            key={ro.value}
                            title={ro.label}
                          >
                            <label>
                              <input
                                value={ro.value}
                                name={ro.name}
                                checked={
                                  !ro.value
                                    ? roleSelected.selection.every(
                                        (res) => !res.isChecked,
                                      )
                                    : roleSelected.selection?.some(
                                        (res) =>
                                          res.id === ro.value && res.isChecked,
                                      )
                                }
                                onChange={(event) =>
                                  onRoleChangeHandler(ro, event)
                                }
                                type="checkbox"
                              />
                              <span> {ro.label} </span>
                            </label>
                          </div>
                        ))}
                      </InfiniteScroll>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              )}
            </Accordion>
          </div>
          <div className="filter-btn-box filter-btn-box--sticky-footer">
            <button
              className={"button button-round button-shadow mr-3"}
              title={t("apply")}
              onClick={handleApply}
            >
              {t("apply")}
            </button>
            <button
              className={
                "button button-round button-border button-dark reset-btn "
              }
              title={t("reset")}
              onClick={handleReset}
            >
              {t("reset")}
            </button>
          </div>
        </StickySidebar>
      )}

      <GroupChannelListProvider
        className={!currentChannel ? "" : "hide-channel-list"}
        onChannelSelect={handleChannelSelect}
        disableAutoSelect={true}
        channelListQueryParams={channelListQuery.channelListQuery}
      >
        <CustomChannelList
          isSdkError={isSdkError}
          currentUser={currentUser}
          selectedOwner={selectedOwner}
          currentChannel={currentChannel}
          setCurrentChannel={setCurrentChannel}
          isTabActive={isTabActive}
          pinnedChannelUrl={pinnedChannelUrl}
          {...customChannelListProps}
          renderHeader={() => (
            <CustomChannelListHeader
              currentUser={currentUser}
              sdk={sdk}
              setCurrentChannel={setCurrentChannel}
              setChannelListQuery={setChannelListQuery}
              selectedOwner={selectedOwner}
              updateChannelList={updateChannelList}
              callNextList={callNextList}
              setCallNextList={setCallNextList}
              selected={selected}
              setOfficeListLoading={setOfficeListLoading}
              setSelected={setSelected}
              setHasMoreOffices={setHasMoreOffices}
              setAllChannelsOfficesForDoctor={setAllChannelsOfficesForDoctor}
              {...customChannelListHeaderProps}
              setFilteredOffices={setFilteredOffices}
              setFilteredRoleIds={setFilteredRoleIds}
              roleSelected={roleSelected}
            />
          )}
          renderChannelPreview={(props) => (
            <CustomChannelPreview
              {...props}
              key={props.channel.url}
              currentUser={currentUser}
              currentChannelUrl={currentChannel?.url}
              setCurrentChannel={setCurrentChannel}
              handleProfileBtnClose={handleProfileBtnClose}
              // external chat only
              {...(externalTabActive && { externalTabActive })}
              profileContactInfo={profileContactInfo}
              sdk={sdk}
            />
          )}
        />
      </GroupChannelListProvider>

      {!(currentChannel && profileContactInfo) && (
        <div
          className={`conversation-wrapper  
                ${!currentChannel ? "" : "show-conversation"}
                ${!profileContactInfo ? "" : "contact-info-active"}`}
        >
          {/* Main Chat */}
          <div className="back-arrow">
            <span className=" link-btn" onClick={handleBackBtn}>
              <img
                src={require("assets/images/arrow-back-icon.svg").default}
                alt="arrow"
              />{" "}
              {t("back")}
            </span>
          </div>
          <GroupChannelProvider
            channelUrl={currentChannel ? currentChannel?.url : null}
            onBeforeSendFileMessage={handleOnBeforeSendFileMessage}
            onBeforeSendUserMessage={handleOnBeforeSendUserMessage}
            isReactionEnabled
          >
            <GroupChannelUI
              renderTypingIndicator={() => <></>}
              renderPlaceholderLoader={() => <ChatLoader />}
              renderPlaceholderEmpty={() => <></>}
              renderPlaceholderInvalid={() => (
                <div className="empty-chat-box">{noChatDetail} </div>
              )}
              renderChannelHeader={() =>
                currentChannel ? (
                  <CustomConversationHeader
                    currentUser={currentUser}
                    channel={currentChannel}
                    handleProfileBtn={handleProfileBtn}
                    setCurrentChannel={setCurrentChannel}
                    sdk={sdk}
                    // external chat only
                    {...customConversationHeaderProps}
                  />
                ) : null
              }
              renderMessage={(props) =>
                currentChannel ? (
                  <CustomMessageItem
                    {...props}
                    t={t}
                    currentUser={currentUser}
                    setFileMessage={setFileMessage}
                    setFileViewer={setFileViewer}
                    currentChannel={currentChannel}
                    // external chat only
                    {...(externalTabActive && { externalTabActive })}
                    // patient and doctor side
                    {...(doctorChatCheckExistInProps && {
                      currentUser,
                      setFileMessage,
                      setFileViewer,
                    })}
                  />
                ) : null
              }
              renderMessageInput={() =>
                currentChannel ? (
                  <CustomMessageInput
                    t={t}
                    sendUserMessage={sendUserMessage}
                    sendFileMessage={sendFileMessage}
                    currentChannel={currentChannel}
                    sdk={sdk}
                    {...(doctorChatCheckExistInProps && {
                      isPatient: true,
                      renderClosedState: <ChatClosed />,
                    })}
                  />
                ) : null
              }
            />
          </GroupChannelProvider>
        </div>
      )}

      {!!(currentChannel && profileContactInfo) && (
        <ContactInfo
          handleProfileBtnClose={handleProfileBtnClose}
          channel={currentChannel}
          currentUser={currentUser}
          setCurrentChannel={setCurrentChannel}
          setFileMessage={setFileMessage}
          setFileViewer={setFileViewer}
          updateChannelList={updateChannelList}
          sdk={sdk}
          // patient and doctor side or internal chat

          {...((internalTabActive || doctorChatCheckExistInProps) && {
            setChannelListQuery,
            selectedOwner,
            setFilteredOffices,
            filteredOffices,
          })}
          {...(doctorChatCheckExistInProps && {
            doctorChatCheck,
            isPatient: true,
          })}
        />
      )}
      {fileViewer && fileMessage && (
        <ImageViewer
          imageUrl={fileMessage.url}
          isOpen={fileViewer}
          closeImageViewerModal={() => setFileViewer(false)}
        />
      )}
    </div>
  );
};

export default withTranslation()(memo(InternalTabContent));
